import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import { applyUiTheme, getCachedUiTheme, cacheUiTheme } from '@/lib/themes';

/** 系统级 UI 主题的 TanStack Query key（管理后台选择器复用同一 key） */
export const UI_THEME_QUERY_KEY = ['system', 'ui-theme'] as const;

/**
 * 系统级 UI 主题 Provider。
 *
 * 职责：订阅 `GET /system/config/ui-theme`，把当前系统主题应用到
 * `<html data-theme="...">`。与个人级明暗 `ThemeProvider`（class="dark"）正交共存。
 *
 * 首屏策略：立即用 localStorage 缓存应用主题，避免等待 API 返回期间的闪烁；
 * API 返回后以服务端为准校正并更新缓存。
 */
export function SystemUiThemeProvider({ children }: { children: ReactNode }) {
  // 首屏：用缓存立即应用，避免主题闪烁
  useEffect(() => {
    applyUiTheme(getCachedUiTheme());
  }, []);

  const { data } = useQuery({
    queryKey: UI_THEME_QUERY_KEY,
    queryFn: () => systemApi.getUiThemeConfig(),
    staleTime: 5 * 60 * 1000,
  });

  // API 返回后以服务端为准校正
  useEffect(() => {
    if (data?.theme) {
      applyUiTheme(data.theme);
      cacheUiTheme(data.theme);
    }
  }, [data?.theme]);

  return <>{children}</>;
}
